import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, Building2, Users2, Rocket } from "lucide-react";
import { QuoteDialog } from "@/components/QuoteDialog";

const TIERS = [
  {
    id: "starter",
    name: "Starter",
    icon: Rocket,
    accent: "#06B6D4",
    tagline: "For pilot teams testing the AI",
    price: "Custom",
    features: [
      "Up to 3 active job posts",
      "AI match score on every applicant",
      "Résumé upload (PDF/DOCX)",
      "Email support",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    icon: Users2,
    accent: "#3B82F6",
    tagline: "For growing recruiting teams",
    highlighted: true,
    price: "Custom",
    features: [
      "Unlimited jobs & applications",
      "Streaming match reports",
      "Structured résumé extraction",
      "Team seats & role controls",
      "Priority support (24h SLA)",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    icon: Building2,
    accent: "#10B981",
    tagline: "For scale, security, and audit",
    price: "Custom",
    features: [
      "SSO / SAML + audit logs",
      "Custom scoring rubrics",
      "ATS integrations",
      "Dedicated success manager",
      "Bespoke SLAs & DPA",
    ],
  },
];

export const PricingSection = () => {
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState("growth");

  const openQuote = (tierId) => {
    setPlan(tierId);
    setOpen(true);
  };

  return (
    <section id="pricing" className="max-w-7xl mx-auto px-6 py-24 scroll-mt-20">
      <div className="mono-label">pricing</div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-1">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">
          Simple, honest, tailored to your team.
        </h2>
        <p className="text-slate-400 max-w-md">
          We price on the shape of your hiring pipeline — not per seat. Tell us about your
          team and we'll send a bespoke quote within one business day.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-4" data-testid="pricing-tiers">
        {TIERS.map((t) => {
          const Icon = t.icon;
          const isHi = t.highlighted;
          return (
            <div
              key={t.id}
              data-testid={`pricing-tier-${t.id}`}
              className={`relative rounded-2xl p-8 border transition-transform hover:-translate-y-1 flex flex-col ${
                isHi
                  ? "border-[#3B82F6]/50 bg-gradient-to-b from-[#111827] to-[#0f1a2e]"
                  : "border-white/10 bg-[#111827]"
              }`}
            >
              {isHi && (
                <span
                  className="absolute -top-3 left-8 mono-label px-2 py-1 rounded-full text-[#0B0F19]"
                  style={{ background: t.accent }}
                >
                  most popular
                </span>
              )}
              <Icon className="w-7 h-7 mb-4" strokeWidth={1.75} style={{ color: t.accent }} />
              <div className="font-display text-2xl font-semibold">{t.name}</div>
              <div className="text-sm text-slate-400 mt-1">{t.tagline}</div>

              <div className="mt-6 pb-6 border-b border-white/10">
                <div className="font-display text-4xl font-semibold">{t.price}</div>
                <div className="text-xs text-slate-400 mt-1">Quote on request · scoped to your team</div>
              </div>

              <ul className="mt-6 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="text-sm text-slate-300 flex gap-2 items-start">
                    <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: t.accent }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => openQuote(t.id)}
                data-testid={`pricing-tier-cta-${t.id}`}
                className={`mt-8 w-full rounded-full py-6 ${
                  isHi
                    ? "bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                    : "bg-transparent border border-white/15 hover:bg-white/5 text-slate-100"
                }`}
              >
                Request quote
              </Button>
            </div>
          );
        })}
      </div>

      <div className="mt-8 text-center text-sm text-slate-400">
        Not sure which fits? {" "}
        <button
          onClick={() => openQuote("custom")}
          data-testid="pricing-generic-cta"
          className="text-[#06B6D4] hover:underline"
        >
          Ask for a custom recommendation →
        </button>
      </div>

      <QuoteDialog open={open} onOpenChange={setOpen} plan={plan} setPlan={setPlan} />
    </section>
  );
};

